import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  Bot,
  Clock,
  Copy,
  Ellipsis,
  History,
  Lock,
  Moon,
  Play,
  Recycle,
  Search,
  ServerOff,
  ShieldCheck,
  ShieldOff,
  Square,
  StickyNote,
  Trash2,
  TriangleAlert,
  Unlock,
  UserPlus,
  X,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AcquireButton } from "@/components/acquire-button";
import { BatchProgressDialog } from "@/components/batch-progress-dialog";
import { BatchRequestProgressDialog } from "@/components/batch-request-progress-dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyableIp } from "@/components/copyable-ip";
import { CopyableMachineId } from "@/components/copyable-machine-id";
import { DisposableMenu } from "@/components/disposable-menu";
import { MachineHistoryDialog } from "@/components/machine-history-dialog";
import { NoteButton, NoteDialog } from "@/components/note-dialog";
import { OwnerAvatar } from "@/components/owner-avatar";
import { PoolPurposeBadge } from "@/components/pool-purpose-badge";
import { ProviderLogo } from "@/components/provider-logo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserResourcesDialog } from "@/components/user-resources-dialog";
import { useTimezone } from "@/contexts/timezone-context";
import {
  useAssignInferbot,
  useCancelMachineRequest,
  useDeleteMachine,
  useMarkMachineProduction,
  useMyMachineRequests,
  useRequestMachine,
  useStartMachine,
  useStopMachine,
  useToggleEodReleaseSkip,
  useToggleMachineDisposable,
  useToggleMachineLock,
  useToggleSkipDelete,
  useTransferMachine,
} from "@/hooks/use-machines";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNow } from "@/hooks/use-now";
import { useUsers } from "@/hooks/use-users";
import { runBatchAcquire, runLooseAcquire } from "@/lib/acquire-actions";
import { formatAge, formatIdleDuration, formatStartedAgo, idleReasonLabel } from "@/lib/format";
import { copyMachinesWithToast } from "@/lib/machine-clipboard";
import { machineHost } from "@/lib/machine-host";
import { poolPurposeLabel } from "@/lib/pool-purpose";
import {
  canClearDisposable,
  canEditDisposableOptions,
  canLooseAcquire,
  canMarkDisposable,
  canStopMachine,
  canToggleDisposable,
  canToggleEodSkip,
  isDisposableRestricted,
  isManagedMachine,
  pendingDisposableFrom,
} from "@/lib/providers";
import { runBatchRelease, runSingleRelease } from "@/lib/release-actions";
import type { BatchActionName, Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

type SortKey =
  | "id"
  | "name"
  | "gpu_type"
  | "provider"
  | "source"
  | "zone"
  | "price_per_hour"
  | "cloud_status"
  | "driver_version"
  | "owner_name"
  | "idle_since";
type SortDirection = "asc" | "desc";

function SortIcon({
  column,
  sortKey,
  sortDirection,
}: {
  column: SortKey;
  sortKey: SortKey | null;
  sortDirection: SortDirection;
}) {
  if (sortKey !== column) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/40" />;
  }
  return sortDirection === "asc" ? (
    <ArrowUp className="ml-1 inline h-3 w-3 text-foreground/70" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3 text-foreground/70" />
  );
}

function getClusterKey(machine: Machine): string | null {
  if (!machine.fabric) return null;
  return `${machine.gpu_type}|${machine.gpu_count}|${machine.fabric}`;
}

const CLUSTER_COLORS = [
  "bg-violet-500",
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-pink-500",
  "bg-indigo-500",
];

function getSortValue(machine: Machine, key: SortKey): string | number {
  switch (key) {
    case "id":
      return machine.id;
    case "name":
    case "gpu_type":
    case "provider":
    case "source":
    case "cloud_status":
      return machine[key];
    case "zone":
      return machine.zone ?? "";
    case "price_per_hour":
      return machine.price_per_hour ?? -1;
    case "driver_version":
      return String(machine.driver_version || "");
    case "owner_name":
      return machine.owner_name ?? "";
    case "idle_since":
      return machine.idle_since ? new Date(machine.idle_since).getTime() : Infinity;
  }
}

function compareValues(
  valueA: string | number,
  valueB: string | number,
  direction: SortDirection,
): number {
  if (typeof valueA === "string" && typeof valueB === "string") {
    const result = valueA.localeCompare(valueB);
    return direction === "asc" ? result : -result;
  }
  const numA = typeof valueA === "number" ? valueA : 0;
  const numB = typeof valueB === "number" ? valueB : 0;
  return direction === "asc" ? numA - numB : numB - numA;
}

/** GPU count and uppercased type, e.g. "8× B200". */
function formatGpu(machine: Machine): string {
  return `${machine.gpu_count}× ${(machine.gpu_type ?? "").toUpperCase()}`;
}

export function TypeBadge({ source }: { source: string }) {
  const map: Record<string, { label: string; className: string }> = {
    spot: {
      label: "Spot",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    },
    pool_spot: {
      label: "Spot",
      className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    },
    reserved: { label: "Reserved", className: "border-primary/30 bg-primary/10 text-primary" },
    manual: { label: "Bare Metal", className: "border-border bg-muted text-muted-foreground" },
    user_created: { label: "User", className: "border-border bg-muted text-muted-foreground" },
    ondemand: { label: "On-Demand", className: "border-border bg-muted text-muted-foreground" },
  };
  const entry = map[source] ?? {
    label: source,
    className: "border-border bg-muted text-muted-foreground",
  };

  return (
    <span
      className={cn(
        "inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium",
        entry.className,
      )}
    >
      {entry.label}
    </span>
  );
}

function StatusDot({
  status,
  isIdle = false,
  idleDuration,
}: {
  status: string;
  isIdle?: boolean;
  idleDuration?: string | null;
}) {
  const label = isIdle ? "idle" : status;
  const dotColor = isIdle
    ? "bg-amber-500"
    : status === "running"
      ? "bg-emerald-500"
      : status === "starting" || status === "stopping"
        ? "bg-amber-500"
        : "bg-muted-foreground/40";
  const textColor = isIdle
    ? "text-amber-600 dark:text-amber-400"
    : status === "running"
      ? "text-emerald-600 dark:text-emerald-400"
      : status === "starting" || status === "stopping"
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-1.5 w-1.5 rounded-full", dotColor)} />
      <span className={cn("text-[13px]", textColor)}>
        {label}
        {isIdle && idleDuration && <span className="ml-1 font-mono">{idleDuration}</span>}
      </span>
    </span>
  );
}

// Status dot with a hover tooltip for secondary context: a specific idle
// reason and/or when the current running session started ("Started N ago").
function StatusWithTooltip({
  status,
  isIdle,
  idleDuration,
  idleReason,
  startedAgo,
  triggerClassName,
}: {
  status: string;
  isIdle: boolean;
  idleDuration: string | null;
  idleReason: string | null | undefined;
  startedAgo: string | null;
  triggerClassName?: string;
}) {
  const reasonLine = isIdle && idleReason ? idleReasonLabel(idleReason) : null;
  const lines = [reasonLine, startedAgo && `Started ${startedAgo} ago`].filter(
    (line): line is string => Boolean(line),
  );
  const dot = <StatusDot status={status} isIdle={isIdle} idleDuration={idleDuration} />;
  if (lines.length === 0) return dot;
  return (
    <Tooltip>
      <TooltipTrigger className={cn("cursor-default", triggerClassName)}>{dot}</TooltipTrigger>
      <TooltipContent>{lines.join("\n")}</TooltipContent>
    </Tooltip>
  );
}

// Format an ISO-8601 UTC timestamp as "2026-06-18 09:44 UTC" (deadline display).
function formatMaintenanceDeadline(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

// Amber warning shown when Nebius has flagged a machine for a maintenance
// restart. Hover reveals the SLA deadline and support ticket.
function MaintenanceWarning({ machine }: { machine: Machine }) {
  if (!machine.maintenance_event_id) return null;
  const deadline = formatMaintenanceDeadline(machine.maintenance_deadline);
  const tooltip = [
    "Nebius flagged this machine for a hardware maintenance restart.",
    deadline ? `Restart (stop+start) required by ${deadline}.` : null,
    machine.maintenance_ticket_id ? `Support ticket: ${machine.maintenance_ticket_id}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip>
      <TooltipTrigger className="inline-flex cursor-default items-center gap-1 self-start">
        <TriangleAlert className="h-3 w-3 text-amber-500" />
        <span className="text-[11px] text-amber-600 dark:text-amber-400">maintenance</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-pre-line">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function LooseAcquireButton({ machine, machines }: { machine: Machine; machines: Machine[] }) {
  const queryClient = useQueryClient();
  const alternatives = machines.filter(
    (candidate) =>
      candidate.id !== machine.id &&
      candidate.cloud_status === "running" &&
      candidate.allocated_to == null &&
      candidate.gpu_count === machine.gpu_count &&
      candidate.gpu_type.toLowerCase() === machine.gpu_type.toLowerCase(),
  );

  return (
    <ConfirmDialog
      title={`Take ${machine.name} from ${machine.owner_name ?? "its owner"}?`}
      description={
        alternatives.length > 0
          ? "Other similar machines are available:"
          : "This machine is disposable and can be taken without the owner’s approval."
      }
      confirmLabel="Take anyway"
      variant="destructive"
      onConfirm={() => runLooseAcquire(machine, queryClient)}
      trigger={
        <Button
          size="sm"
          variant="outline"
          className="h-7 w-[4.25rem] border-orange-300 text-[12px] text-orange-700 hover:bg-orange-500/10 hover:text-orange-800 dark:border-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
        >
          Take
        </Button>
      }
    >
      {alternatives.length > 0 && (
        <div className={cn("mt-1 grid gap-2", alternatives.length > 1 && "sm:grid-cols-2")}>
          {alternatives.map((alternative) => (
            <div
              key={alternative.id}
              className="rounded-xl border border-border/70 bg-muted/40 px-3 py-2.5 shadow-sm"
            >
              <div className="truncate font-mono text-[12px] font-medium text-foreground">
                {alternative.name}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <ProviderLogo provider={alternative.provider} className="px-1.5 py-0 text-[10px]" />
                <span className="font-mono text-[11px] text-muted-foreground">
                  {formatGpu(alternative)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </ConfirmDialog>
  );
}

function FilterInput({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <div className="relative w-full max-w-sm">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Filter machines…"
        aria-label="Filter machines"
        className="h-8 pl-8 pr-8 text-[13px]"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear filter"
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface MachineTableProps {
  machines: Machine[];
  currentUserId: number;
  isAdmin?: boolean;
  showSshString?: boolean;
  extraActions?: (machine: Machine) => React.ReactNode;
  defaultSortKey?: SortKey;
  defaultSortDirection?: SortDirection;
  // Rendered inside the sticky toolbar block, above the filter row, so page
  // headers (e.g. the all-machines filter-chip bar) stick as one block with it.
  stickyHeader?: React.ReactNode;
  // Surface override for the sticky block, e.g. bg-card when inside a card.
  stickyHeaderClassName?: string;
}

// ── Mobile card for a single machine ──

// Provider-action capability sets live in `lib/providers.ts`.

// Recycle marker shared by the desktop name cell and the mobile card: a toggle
// for the holder, a static badge for everyone else.
function DisposableToggle({ machine, currentUserId }: { machine: Machine; currentUserId: number }) {
  // Per-row mutation — a shared one tracks only the latest click, so row B
  // would clear row A's in-flight guard.
  const toggleDisposable = useToggleMachineDisposable();
  const now = useNow();
  const { formatShortTimestamp } = useTimezone();
  const hintId = useId();
  const isDisposable = Boolean(machine.disposable);
  // Marked, but the holder deferred the takeover window — shown dimmer with a
  // clock so a row that can't be taken yet doesn't advertise "Take".
  const pendingFrom = pendingDisposableFrom(machine, now);
  const windowLabel = pendingFrom ? formatShortTimestamp(pendingFrom) : null;
  const icon = (
    <>
      <Recycle className="h-3.5 w-3.5 shrink-0" />
      {windowLabel && <Clock className="-ml-0.5 h-2.5 w-2.5 shrink-0" aria-hidden />}
    </>
  );
  const activeClassName = windowLabel
    ? "text-amber-500 opacity-70 dark:text-amber-400"
    : "text-orange-600 dark:text-orange-400";

  if (!canToggleDisposable(machine, currentUserId)) {
    if (!isDisposable) return null;
    const restricted = isDisposableRestricted(machine);
    return (
      <span
        title={
          windowLabel
            ? `Disposable from ${windowLabel}${restricted ? ", restricted to specific users" : ""}`
            : restricted
              ? "This machine is disposable, but restricted to specific users"
              : "This machine is disposable"
        }
        role="img"
        aria-label="Disposable"
        className={cn("inline-flex shrink-0 items-center p-0.5", activeClassName)}
      >
        {icon}
      </span>
    );
  }

  const isPending = toggleDisposable.isPending;
  const canEditOptions = canEditDisposableOptions(machine, currentUserId);
  const allowedCount = machine.disposable_allowed_user_ids?.length ?? 0;
  const toggle = (
    <Tooltip>
      <TooltipTrigger
        // base-ui's Trigger swallows `disabled` instead of forwarding it to the
        // DOM, so the real button comes through `render`.
        render={<button type="button" disabled={isPending} />}
        // Stable name; `aria-pressed` carries the state (WAI-ARIA APG).
        aria-label="Disposable"
        aria-pressed={isDisposable}
        aria-describedby={windowLabel ? hintId : undefined}
        // Sits next to the machine name link — keep the click off the row.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (isPending) return;
          toggleDisposable.mutate({ id: machine.id, enabled: !isDisposable, name: machine.name });
        }}
        className={cn(
          "inline-flex shrink-0 items-center rounded p-0.5 transition-colors hover:bg-accent disabled:opacity-50",
          isDisposable ? activeClassName : "text-muted-foreground/40 hover:text-muted-foreground",
        )}
      >
        {icon}
        {windowLabel && (
          <span id={hintId} className="sr-only">
            Disposable from {windowLabel}
          </span>
        )}
      </TooltipTrigger>
      {/* Right-click is an invisible affordance — the tooltip is where it's advertised. */}
      <TooltipContent>
        {windowLabel
          ? `Disposable from ${windowLabel}${canEditOptions ? " · right-click to change" : " · click to unmark"}`
          : isDisposable
            ? "Unmark disposable"
            : "Mark disposable"}
        {!windowLabel &&
          canEditOptions &&
          (allowedCount > 0
            ? ` · right-click: ${allowedCount} user${allowedCount === 1 ? "" : "s"} can take it`
            : " · right-click to restrict")}
      </TooltipContent>
    </Tooltip>
  );

  if (!canEditOptions) return toggle;
  return (
    <DisposableMenu machine={machine} currentUserId={currentUserId}>
      {toggle}
    </DisposableMenu>
  );
}

// Moon marker shared by the desktop name cell and the mobile card: a toggle for
// the owner/admin of an allocated machine, a static badge for everyone else.
function EodSkipToggle({
  machine,
  currentUserId,
  isAdmin,
}: {
  machine: Machine;
  currentUserId: number;
  isAdmin: boolean;
}) {
  // Per-row mutation — a shared one tracks only the latest click, so row B
  // would clear row A's in-flight guard.
  const toggle = useToggleEodReleaseSkip();
  const isSkipping = Boolean(machine.eod_release_skip_until);

  if (!canToggleEodSkip(machine, currentUserId, isAdmin)) {
    if (!isSkipping) return null;
    return (
      <Tooltip>
        {/* Non-interactive badge — render a span, not the default button, but
            keep the same styled tooltip as the toggle. */}
        <TooltipTrigger
          render={<span />}
          aria-label="Skip EOD release"
          className="inline-flex shrink-0 items-center p-0.5 text-indigo-500 dark:text-indigo-400"
        >
          <Moon className="h-3.5 w-3.5 shrink-0" />
        </TooltipTrigger>
        <TooltipContent>Skipping tonight's EOD release</TooltipContent>
      </Tooltip>
    );
  }

  const isPending = toggle.isPending;
  return (
    <Tooltip>
      <TooltipTrigger
        // base-ui's Trigger swallows `disabled` instead of forwarding it to the
        // DOM, so the real button comes through `render`.
        render={<button type="button" disabled={isPending} />}
        // Stable name; `aria-pressed` carries the state (WAI-ARIA APG).
        aria-label="Skip EOD release"
        aria-pressed={isSkipping}
        // Sits next to the machine name link — keep the click off the row.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (isPending) return;
          toggle.mutate({ id: machine.id, enabled: !isSkipping, name: machine.name });
        }}
        className={cn(
          "inline-flex shrink-0 items-center rounded p-0.5 transition-colors hover:bg-accent disabled:opacity-50",
          isSkipping
            ? "text-indigo-500 dark:text-indigo-400"
            : "text-muted-foreground/40 hover:text-muted-foreground",
        )}
      >
        <Moon className="h-3.5 w-3.5 shrink-0" />
      </TooltipTrigger>
      <TooltipContent>
        {isSkipping ? "Clear tonight's EOD skip" : "Skip tonight's EOD release"}
      </TooltipContent>
    </Tooltip>
  );
}

// Link nests inside TooltipTrigger (rather than via render=) because base-ui's
// render prop doesn't fire the tooltip when the rendered element is a Link.
// Icons stay outside the trigger: base-ui renders it as a button, which can't
// nest the toggle's own button.
function MachineNameLink({
  machine,
  currentUserId,
  isAdmin,
}: {
  machine: Machine;
  currentUserId: number;
  isAdmin: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger className="block min-w-0 text-left">
          <Link
            to={`/machines/${machine.id}`}
            className="block min-w-0 truncate font-mono text-[13px] font-medium text-foreground hover:underline"
          >
            {machine.name}
          </Link>
        </TooltipTrigger>
        <TooltipContent className="font-mono">
          {machine.name}
          {machine.is_locked ? " (locked)" : ""}
        </TooltipContent>
      </Tooltip>
      {machine.is_locked && (
        <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Locked" />
      )}
      {machine.skip_delete && (
        <ShieldCheck
          className="h-3 w-3 shrink-0 text-muted-foreground"
          aria-label="Protected from the nightly reaper"
        />
      )}
      <DisposableToggle machine={machine} currentUserId={currentUserId} />
      <EodSkipToggle machine={machine} currentUserId={currentUserId} isAdmin={isAdmin} />
    </span>
  );
}

// ── Transfer ownership dialog (admin only) ──

function TransferDialog({
  machine,
  currentUserId,
  mode = "transfer",
  open: openProp,
  onOpenChange,
}: {
  machine: Machine;
  currentUserId: number;
  mode?: "transfer" | "acquire-to";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const transfer = useTransferMachine();
  const { data: users } = useUsers();

  const { buttonLabel, dialogTitle, dialogDescription, confirmLabel } =
    mode === "acquire-to"
      ? {
          buttonLabel: "Acquire to",
          dialogTitle: `Acquire ${machine.name} to`,
          dialogDescription:
            "Acquire this machine for another user. They will be notified via Slack.",
          confirmLabel: "Acquire",
        }
      : {
          buttonLabel: "Transfer",
          dialogTitle: `Transfer ${machine.name}`,
          dialogDescription: "Transfer ownership to another user. They will be notified via Slack.",
          confirmLabel: "Transfer",
        };

  const transferableUsers = (users ?? []).filter(
    (user) =>
      (mode === "acquire-to" || user.id !== machine.allocated_to) &&
      user.id !== currentUserId &&
      !user.is_service,
  );

  return (
    <>
      {!isControlled && (
        <Button
          size="sm"
          variant="outline"
          disabled={transfer.isPending}
          onClick={() => setOpen(true)}
          className="h-7 text-[12px] text-violet-600 hover:bg-violet-500/10 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
        >
          {buttonLabel}
        </Button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-96 rounded-lg border border-border bg-card p-4 shadow-lg">
            <h3 className="text-center text-[14px] font-medium">{dialogTitle}</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">{dialogDescription}</p>
            <Select
              value={selectedUserId}
              onValueChange={(value) => setSelectedUserId(value ?? "")}
            >
              <SelectTrigger className="mt-3 h-8 text-[13px]">
                <SelectValue placeholder="Select user..." />
              </SelectTrigger>
              <SelectContent>
                {transferableUsers.map((user) => (
                  <SelectItem
                    key={user.id}
                    value={String(user.id)}
                    label={user.name}
                    className="text-[13px]"
                  >
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-3 flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[12px]"
                onClick={() => {
                  setOpen(false);
                  setSelectedUserId("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-[12px]"
                disabled={!selectedUserId || transfer.isPending}
                onClick={() => {
                  const targetUser = transferableUsers.find(
                    (user) => String(user.id) === selectedUserId,
                  );
                  transfer.mutate(
                    {
                      id: machine.id,
                      userId: Number(selectedUserId),
                      name: machine.name,
                      targetName: targetUser?.name,
                    },
                    {
                      onSuccess: () => {
                        setOpen(false);
                        setSelectedUserId("");
                      },
                    },
                  );
                }}
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MachineCard({
  machine,
  machines,
  currentUserId,
  isAdmin,
  showSshString,
  extraActions,
  menuMutations,
}: {
  machine: Machine;
  machines: Machine[];
  currentUserId: number;
  isAdmin?: boolean;
  showSshString?: boolean;
  extraActions?: (machine: Machine) => React.ReactNode;
  menuMutations: MachineActionsMenuProps["mutations"];
}) {
  const queryClient = useQueryClient();
  const isFree = !machine.allocated_to && machine.cloud_status === "running";
  const isOwnedByMe = machine.allocated_to === currentUserId;
  const canRelease =
    isOwnedByMe || (isAdmin && machine.allocated_to != null) || machine.owner_is_service;
  const isKicking =
    (isAdmin || machine.owner_is_service) &&
    machine.allocated_to != null &&
    machine.allocated_to !== currentUserId;
  const isOwnedByOther = machine.allocated_to != null && !isOwnedByMe;
  const isStopped = machine.cloud_status === "stopped";
  const idle = formatIdleDuration(machine.idle_since);
  const showIdle = idle !== null && machine.cloud_status === "running";
  const startedAgo =
    machine.cloud_status === "running" ? formatStartedAgo(machine.started_at) : null;

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card p-4 transition-colors",
        isStopped && "opacity-50",
      )}
    >
      {/* Row 1: Name + Status */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {/* The toggle is a button, so it sits beside the Link, not inside it. */}
          <div className="flex min-w-0 items-center gap-1">
            <Link
              to={`/machines/${machine.id}`}
              className="block min-w-0 truncate font-mono text-[14px] font-medium text-foreground hover:underline"
            >
              <span className="mr-1.5 text-muted-foreground">#{machine.id}</span>
              {machine.name}
              {machine.is_locked && (
                <Lock
                  className="ml-1.5 inline-block h-3 w-3 align-[-2px] text-muted-foreground"
                  aria-label="Locked"
                />
              )}
            </Link>
            <DisposableToggle machine={machine} currentUserId={currentUserId} />
            <EodSkipToggle
              machine={machine}
              currentUserId={currentUserId}
              isAdmin={Boolean(isAdmin)}
            />
          </div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {machineHost(machine) ? <CopyableIp ip={machineHost(machine) as string} /> : "\u2014"}
          </div>
          {showSshString && machineHost(machine) && machine.ssh_user && (
            <code className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground">
              ssh {machine.ssh_user}@{machineHost(machine)}
            </code>
          )}
          {machine.k8s_cluster_name && (
            <Link
              to="/k8s-clusters"
              className="mt-0.5 inline-block text-[10px] font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
            >
              K8s: {machine.k8s_cluster_name}
            </Link>
          )}
        </div>
        <StatusWithTooltip
          status={machine.cloud_status}
          isIdle={showIdle}
          idleDuration={idle}
          idleReason={machine.idle_reason}
          startedAgo={startedAgo}
        />
      </div>

      {/* Row 2: GPU + Provider + Type */}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[12px] text-foreground/80">{formatGpu(machine)}</span>
        <span className="text-muted-foreground/30">&middot;</span>
        <ProviderLogo provider={machine.provider} />
        <span className="text-muted-foreground/30">&middot;</span>
        <TypeBadge source={machine.source} />
        {machine.pool_purpose != null && <PoolPurposeBadge purpose={machine.pool_purpose} />}
        {machine.maintenance_event_id && (
          <>
            <span className="text-muted-foreground/30">&middot;</span>
            <MaintenanceWarning machine={machine} />
          </>
        )}
      </div>

      {/* Row 3: Zone + Fabric + Price + Owner + Idle */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        {machine.zone && <span>{machine.zone}</span>}
        {machine.fabric && <span>{machine.fabric}</span>}
        {machine.price_per_hour != null && (
          <span className="font-mono tabular-nums">${machine.price_per_hour.toFixed(2)}/hr</span>
        )}
        {machine.owner_name && machine.allocated_to ? (
          <UserResourcesDialog
            ownerName={machine.owner_name}
            ownerId={machine.allocated_to}
            isService={machine.owner_is_service}
            ownerAvatarUrl={machine.owner_avatar_url}
          >
            <span className="inline-flex items-center gap-1.5">
              <OwnerAvatar
                name={machine.owner_name}
                isService={machine.owner_is_service}
                avatarUrl={machine.owner_avatar_url}
              />
              {machine.owner_name}
            </span>
          </UserResourcesDialog>
        ) : !machine.owner_name && !isStopped ? (
          <span className="italic text-muted-foreground/50">free</span>
        ) : null}
      </div>

      {machine.note && (
        <div className="mt-2 flex items-start gap-1.5 text-[12px] text-muted-foreground">
          <StickyNote className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="min-w-0 break-words">{machine.note}</span>
        </div>
      )}

      {/* Row 4: Actions */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/50 pt-3">
        {extraActions?.(machine)}
        {isFree && <AcquireButton machine={machine} />}
        {canRelease && isKicking && (
          <ConfirmDialog
            title={`Kick ${machine.owner_name} from ${machine.name}?`}
            description={`This will release the machine from ${machine.owner_name}. They will be notified via Slack.`}
            confirmLabel="Kick"
            variant="destructive"
            onConfirm={() => runSingleRelease(machine, queryClient)}
            trigger={
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[12px] border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400 dark:hover:bg-amber-900"
              >
                Kick
              </Button>
            }
          />
        )}
        {canRelease && !isKicking && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => runSingleRelease(machine, queryClient)}
            className="h-7 text-[12px] text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
          >
            Release
          </Button>
        )}
        {isOwnedByOther && !machine.owner_is_service && (
          <TakeOrRequest machine={machine} machines={machines} currentUserId={currentUserId} />
        )}
        <MachineActionsMenu
          machine={machine}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          mutations={menuMutations}
        />
      </div>
    </div>
  );
}

// ── Request a machine from another user ──

function RequestMachineButton({ machine }: { machine: Machine }) {
  const requests = useMyMachineRequests();
  const requestMutation = useRequestMachine();
  const cancelMutation = useCancelMachineRequest();

  const myPendingRequest = requests.data?.find(
    (request) => request.machine_id === machine.id && request.status === "pending",
  );

  if (myPendingRequest) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={cancelMutation.isPending}
        onClick={() =>
          cancelMutation.mutate({
            requestId: myPendingRequest.id,
            machineName: machine.name,
          })
        }
        className="h-7 text-[12px] border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400 dark:hover:bg-amber-900"
        title="Cancel your pending request"
      >
        Cancel request
      </Button>
    );
  }

  return (
    <ConfirmDialog
      title={`Request ${machine.name} from ${machine.owner_name ?? "the owner"}?`}
      description={`A Slack DM will be sent to ${machine.owner_name ?? "the owner"} asking them to release. They can accept, deny, or pick a release time. If they don't respond, they get a reminder after 30 minutes and the machine auto-releases to you 5 minutes later.`}
      confirmLabel="Send request"
      disabled={requestMutation.isPending}
      onConfirm={() => requestMutation.mutate({ id: machine.id, name: machine.name })}
      trigger={
        <Button
          size="sm"
          variant="outline"
          disabled={requestMutation.isPending}
          className="h-7 w-[4.25rem] text-[12px] text-blue-600 hover:bg-blue-500/10 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Request
        </Button>
      }
    />
  );
}

// One place decides Take vs Request, so a row never offers a takeover the
// backend would refuse.
function TakeOrRequest({
  machine,
  machines,
  currentUserId,
}: {
  machine: Machine;
  machines: Machine[];
  currentUserId: number;
}) {
  // Ticking clock so "Take" appears when the window opens, refetch or not.
  const now = useNow();
  const { formatShortTimestamp } = useTimezone();
  if (canLooseAcquire(machine, currentUserId, now)) {
    return <LooseAcquireButton machine={machine} machines={machines} />;
  }
  // "Take it then" only holds for viewers who could take it once the window opens.
  const pendingFrom = pendingDisposableFrom(machine, now);
  if (pendingFrom && canLooseAcquire(machine, currentUserId, Date.parse(pendingFrom))) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex" />}>
          <RequestMachineButton machine={machine} />
        </TooltipTrigger>
        <TooltipContent>
          Disposable from {formatShortTimestamp(pendingFrom)} — request it, or take it then
        </TooltipContent>
      </Tooltip>
    );
  }
  if (isDisposableRestricted(machine)) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex" />}>
          <RequestMachineButton machine={machine} />
        </TooltipTrigger>
        <TooltipContent>Disposable, but the owner restricted it to specific users</TooltipContent>
      </Tooltip>
    );
  }
  return <RequestMachineButton machine={machine} />;
}

// ── Stop with optional delete ──

function StopWithDeleteDialog({
  machine,
  stop,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  machine: Machine;
  stop: ReturnType<typeof useStopMachine>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [deleteAfterStop, setDeleteAfterStop] = useState(false);
  const showDeleteOption = isManagedMachine(machine);
  const isOnDemand = machine.source === "ondemand";

  // For on-demand machines, append a notable warning under the standard
  // line: capacity isn't guaranteed and getting one back can be hard.
  let description: React.ReactNode;
  if (deleteAfterStop) {
    description = "The machine will be stopped and then permanently deleted.";
  } else if (isOnDemand) {
    description = (
      <>
        The machine will be stopped. You can start it again later.
        <span className="mt-2 block text-amber-700 dark:text-amber-400">
          <strong>Warning:</strong> stop on-demand machines only when needed — they're actively
          used, and capacity isn't always available to get one back.
        </span>
      </>
    );
  } else {
    description = "The machine will be stopped. You can start it again later.";
  }

  return (
    <ConfirmDialog
      open={openProp}
      title={`Stop ${machine.name}?`}
      description={description}
      confirmLabel={deleteAfterStop ? "Stop & Delete" : "Stop"}
      variant="destructive"
      disabled={stop.isPending}
      onConfirm={() => stop.mutate({ id: machine.id, name: machine.name, deleteAfterStop })}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setDeleteAfterStop(false);
        onOpenChangeProp?.(nextOpen);
      }}
      trigger={
        openProp === undefined ? (
          <Button
            size="sm"
            variant="outline"
            disabled={stop.isPending}
            className="h-7 text-[12px] text-muted-foreground hover:text-foreground"
          >
            Stop
          </Button>
        ) : undefined
      }
    >
      {showDeleteOption && (
        <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <input
            type="checkbox"
            checked={deleteAfterStop}
            onChange={(event) => setDeleteAfterStop(event.target.checked)}
            className="rounded border-border"
          />
          Also delete this machine (terminates cloud instance permanently)
        </label>
      )}
    </ConfirmDialog>
  );
}

// ── Ellipsis actions menu for secondary row actions ──

interface MachineActionsMenuProps {
  machine: Machine;
  currentUserId: number;
  isAdmin?: boolean;
  mutations: {
    stop: ReturnType<typeof useStopMachine>;
    start: ReturnType<typeof useStartMachine>;
    deleteMachine: ReturnType<typeof useDeleteMachine>;
    assignInferbot: ReturnType<typeof useAssignInferbot>;
    toggleEodReleaseSkip: ReturnType<typeof useToggleEodReleaseSkip>;
    toggleLock: ReturnType<typeof useToggleMachineLock>;
    toggleDisposable: ReturnType<typeof useToggleMachineDisposable>;
    toggleSkipDelete: ReturnType<typeof useToggleSkipDelete>;
    markProduction: ReturnType<typeof useMarkMachineProduction>;
  };
}

type OpenDialog =
  | "history"
  | "note"
  | "stop"
  | "start"
  | "delete"
  | "assign"
  | "transfer"
  | "acquireTo"
  | "lockMachine"
  | "markProduction"
  | null;

function MachineActionsMenu({
  machine,
  currentUserId,
  isAdmin,
  mutations,
}: MachineActionsMenuProps) {
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const closeDialog = () => setOpenDialog(null);

  const {
    stop,
    start,
    deleteMachine,
    assignInferbot,
    toggleEodReleaseSkip,
    toggleLock,
    toggleDisposable,
    toggleSkipDelete,
    markProduction,
  } = mutations;

  const isFree = !machine.allocated_to && machine.cloud_status === "running";
  const isOwnedByMe = machine.allocated_to === currentUserId;
  const isStopped = machine.cloud_status === "stopped";
  const isLocked = Boolean(machine.is_locked);
  // A note only exists while a machine is held; the holder or an admin may edit.
  const canEditNote = machine.allocated_to != null && (isOwnedByMe || isAdmin);
  const canStart =
    isStopped &&
    isManagedMachine(machine) &&
    (machine.allocated_to == null || isOwnedByMe || isAdmin);
  // Destructive actions and ownership changes are blocked by the lock —
  // hide them in the dropdown so the menu doesn't dangle items that 423.
  // Owner-self carve-outs (release, assign-inferbot) preserve the lock's
  // "owner can opt out" semantics.
  const canDelete =
    isStopped &&
    isManagedMachine(machine) &&
    (machine.allocated_to == null || isOwnedByMe || isAdmin) &&
    !isLocked;
  const showStop = canStopMachine(machine, currentUserId, Boolean(isAdmin));
  const showAssign =
    (isFree || (isOwnedByMe && machine.cloud_status === "running")) && (!isLocked || isOwnedByMe);
  const showTransfer =
    isAdmin && machine.cloud_status === "running" && machine.allocated_to != null && !isLocked;
  const showAcquireTo = isAdmin && isFree && !isLocked;
  const showSkipEod =
    machine.cloud_status === "running" && machine.allocated_to != null && (isOwnedByMe || isAdmin);
  const eodSkipActive = Boolean(machine.eod_release_skip_until);
  const showLock = Boolean(isAdmin);
  // Admin-only: mark as production removes the machine from Fleet entirely.
  const showProduction = Boolean(isAdmin);
  // Same predicate as the row toggle, so the menu never offers what the backend rejects.
  const showDisposable = canToggleDisposable(machine, currentUserId);

  const hasSecondaryActions =
    showAssign ||
    showStop ||
    canStart ||
    canDelete ||
    showTransfer ||
    showAcquireTo ||
    showSkipEod ||
    showLock ||
    showProduction ||
    showDisposable;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
          <Ellipsis className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px]">
          <DropdownMenuItem onClick={() => setOpenDialog("history")}>
            <History className="h-3.5 w-3.5" />
            History
          </DropdownMenuItem>
          {canEditNote && (
            <DropdownMenuItem onClick={() => setOpenDialog("note")}>
              <StickyNote className="h-3.5 w-3.5" />
              {machine.note ? "Edit note" : "Add note"}
            </DropdownMenuItem>
          )}
          {hasSecondaryActions && <DropdownMenuSeparator />}
          {showAssign && (
            <DropdownMenuItem onClick={() => setOpenDialog("assign")}>
              <Bot className="h-3.5 w-3.5" />
              Assign inferbot
            </DropdownMenuItem>
          )}
          {showStop && (
            <DropdownMenuItem onClick={() => setOpenDialog("stop")}>
              <Square className="h-3.5 w-3.5" />
              Stop
            </DropdownMenuItem>
          )}
          {canStart && (
            <DropdownMenuItem onClick={() => setOpenDialog("start")}>
              <Play className="h-3.5 w-3.5" />
              Start
            </DropdownMenuItem>
          )}
          {showTransfer && (
            <DropdownMenuItem onClick={() => setOpenDialog("transfer")}>
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Transfer
            </DropdownMenuItem>
          )}
          {showAcquireTo && (
            <DropdownMenuItem onClick={() => setOpenDialog("acquireTo")}>
              <UserPlus className="h-3.5 w-3.5" />
              Acquire to
            </DropdownMenuItem>
          )}
          {showLock && (
            <DropdownMenuItem onClick={() => setOpenDialog("lockMachine")}>
              {isLocked ? (
                <>
                  <Unlock className="h-3.5 w-3.5" />
                  Unlock machine
                </>
              ) : (
                <>
                  <Lock className="h-3.5 w-3.5" />
                  Lock machine
                </>
              )}
            </DropdownMenuItem>
          )}
          {/* Un-protecting is admin-only server-side (it re-arms an
              irreversible delete); protecting is open to everyone. */}
          {(!machine.skip_delete || isAdmin) && (
            <DropdownMenuItem
              onClick={() =>
                toggleSkipDelete.mutate({
                  id: machine.id,
                  enabled: !machine.skip_delete,
                  name: machine.name,
                })
              }
            >
              {machine.skip_delete ? (
                <>
                  <ShieldOff className="h-3.5 w-3.5" />
                  Allow auto-delete
                </>
              ) : (
                <>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Protect from deletion
                </>
              )}
            </DropdownMenuItem>
          )}
          {showProduction && (
            <DropdownMenuItem onClick={() => setOpenDialog("markProduction")}>
              <ServerOff className="h-3.5 w-3.5" />
              Mark as production
            </DropdownMenuItem>
          )}
          {showDisposable && (
            <DropdownMenuCheckboxItem
              checked={Boolean(machine.disposable)}
              disabled={toggleDisposable.isPending && toggleDisposable.variables?.id === machine.id}
              onCheckedChange={(next) =>
                toggleDisposable.mutate({
                  id: machine.id,
                  enabled: next,
                  name: machine.name,
                })
              }
            >
              <Recycle className="h-3.5 w-3.5" />
              Disposable
            </DropdownMenuCheckboxItem>
          )}
          {showSkipEod && (
            <DropdownMenuCheckboxItem
              checked={eodSkipActive}
              disabled={
                toggleEodReleaseSkip.isPending && toggleEodReleaseSkip.variables?.id === machine.id
              }
              onCheckedChange={(next) =>
                toggleEodReleaseSkip.mutate({
                  id: machine.id,
                  enabled: next,
                  name: machine.name,
                })
              }
            >
              <Moon className="h-3.5 w-3.5" />
              Skip EOD
            </DropdownMenuCheckboxItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setOpenDialog("delete")}>
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MachineHistoryDialog
        machine={machine}
        open={openDialog === "history"}
        onOpenChange={(next) => !next && closeDialog()}
      />

      <NoteDialog
        machines={[machine]}
        open={openDialog === "note"}
        onOpenChange={(next) => !next && closeDialog()}
      />

      <ConfirmDialog
        open={openDialog === "assign"}
        onOpenChange={(next) => !next && closeDialog()}
        title={`Assign ${machine.name} to inferbot?`}
        description="The machine will be assigned to the inferbot service account."
        confirmLabel="Assign"
        disabled={assignInferbot.isPending}
        onConfirm={() => assignInferbot.mutate({ id: machine.id, name: machine.name })}
      />

      <StopWithDeleteDialog
        machine={machine}
        stop={stop}
        open={openDialog === "stop"}
        onOpenChange={(next) => !next && closeDialog()}
      />

      <ConfirmDialog
        open={openDialog === "start"}
        onOpenChange={(next) => !next && closeDialog()}
        title={`Start ${machine.name}?`}
        description="The machine will be started."
        confirmLabel="Start"
        disabled={start.isPending}
        onConfirm={() => start.mutate({ id: machine.id, name: machine.name })}
      />

      <ConfirmDialog
        open={openDialog === "delete"}
        onOpenChange={(next) => !next && closeDialog()}
        title={`Delete ${machine.name}?`}
        description="This will permanently terminate the cloud instance and delete its boot disk. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        disabled={deleteMachine.isPending}
        onConfirm={() => deleteMachine.mutate({ id: machine.id, name: machine.name })}
      />

      <TransferDialog
        machine={machine}
        currentUserId={currentUserId}
        open={openDialog === "transfer"}
        onOpenChange={(next) => !next && closeDialog()}
      />

      <TransferDialog
        machine={machine}
        currentUserId={currentUserId}
        mode="acquire-to"
        open={openDialog === "acquireTo"}
        onOpenChange={(next) => !next && closeDialog()}
      />

      <ConfirmDialog
        open={openDialog === "lockMachine"}
        onOpenChange={(next) => !next && closeDialog()}
        title={isLocked ? `Unlock ${machine.name}?` : `Lock ${machine.name}?`}
        description={
          isLocked
            ? "Re-enables EOD/idle release, peer requests, CI watchdog auto-release, and admin destructive actions (kick, transfer, stop, delete, reboot, assign-inferbot)."
            : "Blocks EOD/idle release, peer requests, CI watchdog auto-release, and admin destructive actions (kick, transfer, stop, delete, reboot, assign-inferbot). Owner self-release / self-reboot / self-assign-to-inferbot still work."
        }
        confirmLabel={isLocked ? "Unlock" : "Lock"}
        disabled={toggleLock.isPending}
        onConfirm={() =>
          toggleLock.mutate({ id: machine.id, enabled: !isLocked, name: machine.name })
        }
      />

      <ConfirmDialog
        open={openDialog === "markProduction"}
        onOpenChange={(next) => !next && closeDialog()}
        title={`Mark ${machine.name} as production?`}
        description="Removes the machine from Fleet — it will no longer be listed, acquirable, or touched by EOD/idle/spot jobs. The cloud instance keeps running. To bring it back, remove its instance ID from the Production machines list in Settings."
        confirmLabel="Mark production"
        variant="destructive"
        disabled={markProduction.isPending}
        onConfirm={() => markProduction.mutate({ id: machine.id, name: machine.name })}
      />
    </>
  );
}

// ── Sort dropdown for mobile ──

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "id", label: "ID" },
  { value: "name", label: "Name" },
  { value: "gpu_type", label: "GPU" },
  { value: "cloud_status", label: "Status" },
  { value: "owner_name", label: "Owner" },
  { value: "idle_since", label: "Idle" },
  { value: "price_per_hour", label: "Price" },
];

// Several MachineTable instances can be mounted at once, each with its own selection;
// the global Cmd/Ctrl+C / Cmd/Ctrl+A shortcuts act only on the last-touched table.
let activeMachineTableId: string | null = null;

// ── Main component ──

export function MachineTable({
  machines,
  currentUserId,
  isAdmin,
  showSshString,
  extraActions,
  defaultSortKey,
  defaultSortDirection = "asc",
  stickyHeader,
  stickyHeaderClassName,
}: MachineTableProps) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const menuMutations = {
    stop: useStopMachine(),
    start: useStartMachine(),
    deleteMachine: useDeleteMachine(),
    assignInferbot: useAssignInferbot(),
    toggleEodReleaseSkip: useToggleEodReleaseSkip(),
    toggleLock: useToggleMachineLock(),
    toggleDisposable: useToggleMachineDisposable(),
    toggleSkipDelete: useToggleSkipDelete(),
    markProduction: useMarkMachineProduction(),
  };
  const [sortKey, setSortKey] = useState<SortKey | null>(defaultSortKey ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filterText, setFilterText] = useState("");
  // Selection captured at the first click of a shift-click sequence, so a
  // shift+double-click can toggle the whole fabric from the pre-gesture state
  // even though the first click already toggled the unassigned machines.
  const preClusterClickSelection = useRef<Set<number> | null>(null);

  // Batch progress/confirm dialog state — at most one of these is open at a time.
  const [batchProgress, setBatchProgress] = useState<{
    action: BatchActionName;
    machines: Machine[];
    deleteAfterStop?: boolean;
  } | null>(null);
  const [batchRequest, setBatchRequest] = useState<{ machines: Machine[] } | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    action: BatchActionName;
    machines: Machine[];
    deleteAfterStop?: boolean;
    title: string;
    description: string;
    destructive: boolean;
  } | null>(null);
  const [pendingRequestMachines, setPendingRequestMachines] = useState<Machine[] | null>(null);
  const [batchNoteMachines, setBatchNoteMachines] = useState<Machine[] | null>(null);

  const filteredMachines = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return machines;
    return machines.filter((machine) => {
      const haystack = [
        String(machine.id),
        machine.name,
        machineHost(machine) ?? "",
        machine.gpu_type,
        String(machine.gpu_count),
        `${machine.gpu_count}x${machine.gpu_type}`,
        `${machine.gpu_count}\u00d7${machine.gpu_type}`,
        machine.provider,
        machine.source,
        machine.pool_purpose != null ? poolPurposeLabel(machine.pool_purpose) : "",
        machine.zone ?? "",
        machine.fabric ?? "",
        machine.cloud_status,
        machine.driver_version ?? "",
        machine.owner_name ?? "",
        machine.k8s_cluster_name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [machines, filterText]);

  // Prune selection to visible rows so batch actions and count displays never
  // reference rows the user can no longer see.
  useEffect(() => {
    setSelectedIds((previous) => {
      if (previous.size === 0) return previous;
      const visible = new Set(filteredMachines.map((machine) => machine.id));
      let changed = false;
      const next = new Set<number>();
      for (const id of previous) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : previous;
    });
  }, [filteredMachines]);

  // Compute cluster info: machines with same gpu_type + gpu_count + fabric form a cluster
  const clusterInfo = useMemo(() => {
    const clusterMap = new Map<string, Machine[]>();
    const machineToCluster = new Map<number, string>();
    const clusterColors = new Map<string, string>();

    for (const machine of filteredMachines) {
      const key = getClusterKey(machine);
      if (!key) continue;
      const existing = clusterMap.get(key) ?? [];
      existing.push(machine);
      clusterMap.set(key, existing);
      machineToCluster.set(machine.id, key);
    }

    // Filter out "clusters" with only one machine (not really a cluster)
    for (const [key, machines] of clusterMap) {
      if (machines.length < 2) {
        clusterMap.delete(key);
        for (const machine of machines) {
          machineToCluster.delete(machine.id);
        }
      }
    }

    // Assign colors to clusters
    let colorIndex = 0;
    for (const key of clusterMap.keys()) {
      clusterColors.set(key, CLUSTER_COLORS[colorIndex % CLUSTER_COLORS.length] ?? "bg-violet-500");
      colorIndex++;
    }

    return { clusterMap, machineToCluster, clusterColors };
  }, [filteredMachines]);

  const toggleSelect = useCallback((machineId: number) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  }, []);

  // Shift+click toggles the fabric's unassigned machines — the ones not currently
  // allocated to anyone (what you'd actually grab). Falls back to the whole cluster
  // when nothing is unassigned so a shift+click is never a silent no-op.
  const toggleUnassignedInCluster = useCallback(
    (clusterKey: string) => {
      const clusterMachines = clusterInfo.clusterMap.get(clusterKey) ?? [];
      const unassigned = clusterMachines.filter((machine) => !machine.allocated_to);
      const target = unassigned.length > 0 ? unassigned : clusterMachines;
      setSelectedIds((previous) => {
        const next = new Set(previous);
        const allSelected = target.every((machine) => previous.has(machine.id));
        for (const machine of target) {
          if (allSelected) next.delete(machine.id);
          else next.add(machine.id);
        }
        return next;
      });
    },
    [clusterInfo],
  );

  // Shift+double-click toggles the entire fabric. "baseline" is the selection from
  // before this gesture's first click, so we decide select-vs-deselect from the
  // user's intent — not from the half-applied state the first click left behind.
  const toggleEntireCluster = useCallback(
    (clusterKey: string, baseline: Set<number> | null) => {
      const clusterMachines = clusterInfo.clusterMap.get(clusterKey) ?? [];
      setSelectedIds((previous) => {
        const reference = baseline ?? previous;
        const allSelected = clusterMachines.every((machine) => reference.has(machine.id));
        const next = new Set(previous);
        for (const machine of clusterMachines) {
          if (allSelected) next.delete(machine.id);
          else next.add(machine.id);
        }
        return next;
      });
    },
    [clusterInfo],
  );

  // Plain click toggles the single machine. Shift+click toggles the fabric's
  // unassigned machines; shift+double-click toggles the entire fabric. event.detail
  // is 2 on the second click of a double-click, so both fire straight from this
  // handler with no debounce — keeping the single shift+click instant. The first
  // click snapshots the selection so the second can toggle from the pre-gesture state.
  const handleClusterCheckboxClick = useCallback(
    (machineId: number, clusterKey: string, event: React.MouseEvent) => {
      event.preventDefault();
      if (!event.shiftKey) {
        toggleSelect(machineId);
      } else if (event.detail >= 2) {
        toggleEntireCluster(clusterKey, preClusterClickSelection.current);
      } else {
        preClusterClickSelection.current = new Set(selectedIds);
        toggleUnassignedInCluster(clusterKey);
      }
    },
    [selectedIds, toggleSelect, toggleUnassignedInCluster, toggleEntireCluster],
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedMachines = useMemo(() => {
    const sorted = [...filteredMachines];

    if (sortKey) {
      sorted.sort((machineA, machineB) => {
        const primary = compareValues(
          getSortValue(machineA, sortKey),
          getSortValue(machineB, sortKey),
          sortDirection,
        );
        if (primary !== 0) return primary;
        // The Status column merges cloud_status with idle info, so break
        // ties on the visible status by idle_since.
        if (sortKey === "cloud_status") {
          return compareValues(
            getSortValue(machineA, "idle_since"),
            getSortValue(machineB, "idle_since"),
            sortDirection,
          );
        }
        return 0;
      });
    } else {
      // Default sort: group by cluster (gpu_type + gpu_count + fabric), then by name
      sorted.sort((machineA, machineB) => {
        const clusterA = getClusterKey(machineA) ?? "";
        const clusterB = getClusterKey(machineB) ?? "";
        if (clusterA !== clusterB) {
          // Put machines with clusters before those without
          if (!clusterA) return 1;
          if (!clusterB) return -1;
          return clusterA.localeCompare(clusterB);
        }
        return machineA.name.localeCompare(machineB.name);
      });
    }

    return sorted;
  }, [filteredMachines, sortKey, sortDirection]);

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedMachines.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedMachines.map((machine) => machine.id)));
    }
  };

  const selectedMachines = sortedMachines.filter((machine) => selectedIds.has(machine.id));
  // Per-row buttons gate destructive actions on ownership / admin override —
  // mirror that here so the toolbar doesn't advertise actions the backend
  // will 403 on.
  const ownsOrAdmin = (machine: Machine) => machine.allocated_to === currentUserId || isAdmin;
  // A note can only be edited on a machine you hold (or any held machine, if admin).
  const canEditNote = (machine: Machine) => machine.allocated_to != null && ownsOrAdmin(machine);
  const acquireEligible = selectedMachines.filter(
    (machine) => machine.cloud_status === "running" && machine.allocated_to == null,
  );
  const stopEligible = selectedMachines.filter((machine) =>
    canStopMachine(machine, currentUserId, Boolean(isAdmin)),
  );
  const startEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "stopped" &&
      isManagedMachine(machine) &&
      (machine.allocated_to == null || ownsOrAdmin(machine)),
  );
  const releaseEligible = selectedMachines.filter((machine) => machine.allocated_to != null);
  const noteEligible = selectedMachines.filter(canEditNote);
  const deleteEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "stopped" &&
      isManagedMachine(machine) &&
      (machine.allocated_to == null ? isAdmin : ownsOrAdmin(machine)),
  );
  const rebootEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "running" &&
      machine.allocated_to != null &&
      isManagedMachine(machine) &&
      ownsOrAdmin(machine),
  );
  // EOD skip is a lifecycle flag, not a cloud op — no provider gate. Mirrors
  // the per-row toggle (running + allocated + owns/admin), split by whether a
  // skip is already active so each button only targets the rows it can change.
  const skipEodEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "running" &&
      machine.allocated_to != null &&
      ownsOrAdmin(machine) &&
      !machine.eod_release_skip_until,
  );
  const clearEodSkipEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "running" &&
      machine.allocated_to != null &&
      ownsOrAdmin(machine) &&
      Boolean(machine.eod_release_skip_until),
  );
  // Holder-only — no admin override, unlike EOD skip above.
  const markDisposableEligible = selectedMachines.filter((machine) =>
    canMarkDisposable(machine, currentUserId),
  );
  const clearDisposableEligible = selectedMachines.filter((machine) =>
    canClearDisposable(machine, currentUserId),
  );
  // Already-pending duplicates aren't filtered here — backend rejects them
  // per-row, surfaced in the progress dialog.
  const requestEligible = selectedMachines.filter(
    (machine) =>
      machine.cloud_status === "running" &&
      machine.allocated_to != null &&
      machine.allocated_to !== currentUserId &&
      !machine.owner_is_service,
  );
  const releaseKickCount = releaseEligible.filter(
    (machine) => machine.allocated_to !== currentUserId,
  ).length;

  const machineNamesPreview = (machines: Machine[]): string => {
    const names = machines.slice(0, 5).map((machine) => machine.name);
    if (machines.length > 5) names.push(`+${machines.length - 5} more`);
    return names.join(", ");
  };

  const startBatchAcquire = () => {
    runBatchAcquire(acquireEligible, queryClient);
    setSelectedIds(new Set());
  };

  // One "name,ip" line per selected machine via the shared helper, so the table
  // copy stays in lockstep with the row copy button and the acquire flow.
  const copySelected = () => copyMachinesWithToast(selectedMachines);

  // Cmd/Ctrl+C copies the selected machines and Cmd/Ctrl+A acquires the
  // acquire-eligible ones, each mirroring its toolbar button. Refs keep the
  // keydown handler pointed at the latest closures/count so the listener only
  // re-subscribes when the selection appears or clears, and the effect deps stay
  // stable instead of having to list per-render callbacks (which would warn).
  const tableInstanceId = useId();
  const copySelectedRef = useRef(copySelected);
  copySelectedRef.current = copySelected;
  const acquireSelectedRef = useRef(startBatchAcquire);
  acquireSelectedRef.current = startBatchAcquire;
  const acquireEligibleCountRef = useRef(acquireEligible.length);
  acquireEligibleCountRef.current = acquireEligible.length;
  const hasSelection = selectedIds.size > 0;
  useEffect(() => {
    if (!hasSelection) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      if (key !== "c" && key !== "a") return;
      // Only the table the user last clicked into owns the shortcut, so two
      // mounted tables with selections don't both fire and race each other.
      if (activeMachineTableId !== tableInstanceId) return;
      // Leave the native shortcut alone when the user is editing a field or has
      // highlighted text — only hijack it for the bare selection.
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA"
      ) {
        return;
      }
      const textSelection = window.getSelection();
      if (textSelection && !textSelection.isCollapsed && textSelection.toString().length > 0) {
        return;
      }
      if (key === "c") {
        event.preventDefault();
        void copySelectedRef.current();
      } else if (acquireEligibleCountRef.current > 0) {
        // Only claim Cmd/Ctrl+A when there's something to acquire; otherwise
        // leave the browser's Select-All intact.
        event.preventDefault();
        acquireSelectedRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasSelection, tableInstanceId]);

  const requestOwnersPreview = (machines: Machine[]): string =>
    Array.from(new Set(machines.map((machine) => machine.owner_name ?? "(unknown)"))).join(", ");

  const startBatch = (
    action: BatchActionName,
    machines: Machine[],
    options?: { deleteAfterStop?: boolean },
  ) => {
    const deleteAfterStop = options?.deleteAfterStop;
    const isDestructive =
      action === "delete" ||
      action === "reboot" ||
      (action === "stop" && deleteAfterStop) ||
      (action === "release" && releaseKickCount > 0);

    if (!isDestructive) {
      // Release uses non-blocking stacking toasts; other actions use the modal.
      if (action === "release") {
        runBatchRelease(machines, queryClient);
      } else {
        setBatchProgress({ action, machines, deleteAfterStop });
      }
      setSelectedIds(new Set());
      return;
    }

    const verbMap: Record<BatchActionName, string> = {
      release: "Release",
      stop: deleteAfterStop ? "Stop and delete" : "Stop",
      start: "Start",
      delete: "Permanently delete",
      reboot: "Reboot",
      skip_eod: "Skip EOD",
      unskip_eod: "Clear EOD skip",
      mark_disposable: "Mark disposable",
      unmark_disposable: "Clear disposable",
    };
    const verb = verbMap[action];
    const noun = `${machines.length} machine${machines.length === 1 ? "" : "s"}`;
    const kickWarning =
      action === "release" && releaseKickCount > 0
        ? ` (${releaseKickCount} owned by another user — they will be kicked)`
        : "";
    setPendingConfirm({
      action,
      machines,
      deleteAfterStop,
      destructive: true,
      title: `${verb} ${noun}?`,
      description: `${machineNamesPreview(machines)}${kickWarning}`,
    });
  };

  const hasFilter = filterText.trim().length > 0;

  // ── Mobile: card list ──
  if (isMobile) {
    return (
      <>
        {stickyHeader}
        <div className="flex flex-col gap-2 px-4 py-3">
          <FilterInput value={filterText} onChange={setFilterText} />
          {/* Sort control */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Sort by
            </span>
            <Select
              value={sortKey ?? ""}
              onValueChange={(value) => {
                if (value) {
                  if (value === sortKey) {
                    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                  } else {
                    setSortKey(value as SortKey);
                    setSortDirection("asc");
                  }
                }
              }}
            >
              <SelectTrigger className="h-7 w-28 text-[12px]">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-[12px]">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sortKey && (
              <button
                type="button"
                onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                className="text-[11px] text-muted-foreground hover:text-foreground"
              >
                {sortDirection === "asc" ? "\u2191" : "\u2193"}
              </button>
            )}
          </div>

          {/* Cards */}
          {sortedMachines.map((machine) => (
            <MachineCard
              key={machine.id}
              machine={machine}
              machines={machines}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              showSshString={showSshString}
              extraActions={extraActions}
              menuMutations={menuMutations}
            />
          ))}
          {sortedMachines.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              {hasFilter ? "No machines match your filter" : "No machines found"}
            </div>
          )}
        </div>
      </>
    );
  }

  // ── Desktop: table ──
  const headerClass =
    "text-[11px] uppercase tracking-wider text-muted-foreground font-medium cursor-pointer select-none hover:text-foreground/70 transition-colors";

  return (
    <div
      onPointerDownCapture={() => {
        activeMachineTableId = tableInstanceId;
      }}
      onFocusCapture={() => {
        activeMachineTableId = tableInstanceId;
      }}
    >
      {/* Sticky block (page header slot + filter/bulk-action row). Lives outside
          the Table's overflow-x-auto wrapper, which position:sticky can't escape. */}
      <div
        className={cn(
          "sticky top-0 z-20 border-b border-border bg-background",
          stickyHeaderClassName,
        )}
      >
        {stickyHeader}
        <div className="flex items-center gap-2 px-6 pt-3 pb-2">
          <div className="w-72 shrink-0">
            <FilterInput value={filterText} onChange={setFilterText} />
          </div>
          {/* The selection strip scrolls horizontally instead of wrapping — a wrap
              would grow the sticky header and push the table down on narrow screens. */}
          {hasSelection && (
            <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [&>*]:shrink-0">
              <span className="px-1 text-[13px] font-medium tabular-nums">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-[12px] text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
              <span className="mx-1 text-border">|</span>
              <Button
                size="sm"
                variant="outline"
                onClick={copySelected}
                title="Copy selected machines (⌘C / Ctrl+C)"
                className="h-7 gap-1 text-[12px] text-muted-foreground hover:text-foreground"
              >
                <Copy className="h-3 w-3" />
                Copy ({selectedMachines.length})
              </Button>
              {noteEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBatchNoteMachines(noteEligible)}
                  className="h-7 gap-1 text-[12px] text-muted-foreground hover:text-foreground"
                >
                  <StickyNote className="h-3 w-3" />
                  Note ({noteEligible.length})
                </Button>
              )}
              {acquireEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startBatchAcquire}
                  title="Acquire selected machines (⌘A / Ctrl+A)"
                  className="h-7 text-[12px] text-primary hover:bg-primary/10 hover:text-primary"
                >
                  Acquire ({acquireEligible.length})
                </Button>
              )}
              {stopEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("stop", stopEligible)}
                  className="h-7 text-[12px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
                >
                  Stop ({stopEligible.length})
                </Button>
              )}
              {requestEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPendingRequestMachines(requestEligible)}
                  className="h-7 text-[12px] text-blue-600 hover:bg-blue-500/10 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Request ({requestEligible.length})
                </Button>
              )}
              {rebootEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("reboot", rebootEligible)}
                  className="h-7 text-[12px] text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
                >
                  Reboot ({rebootEligible.length})
                </Button>
              )}
              {skipEodEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("skip_eod", skipEodEligible)}
                  className="h-7 text-[12px] text-indigo-600 hover:bg-indigo-500/10 dark:text-indigo-400"
                >
                  Skip EOD ({skipEodEligible.length})
                </Button>
              )}
              {clearEodSkipEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("unskip_eod", clearEodSkipEligible)}
                  className="h-7 text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Clear EOD skip ({clearEodSkipEligible.length})
                </Button>
              )}
              {markDisposableEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("mark_disposable", markDisposableEligible)}
                  className="h-7 text-[12px] text-orange-600 hover:bg-orange-500/10 dark:text-orange-400"
                >
                  Disposable ({markDisposableEligible.length})
                </Button>
              )}
              {clearDisposableEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("unmark_disposable", clearDisposableEligible)}
                  className="h-7 text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Clear disposable ({clearDisposableEligible.length})
                </Button>
              )}
              {startEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("start", startEligible)}
                  className="h-7 text-[12px] text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
                >
                  Start ({startEligible.length})
                </Button>
              )}
              {releaseEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("release", releaseEligible)}
                  className="h-7 text-[12px] text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
                >
                  {releaseKickCount > 0 ? "Kick + Release" : "Release"} ({releaseEligible.length})
                </Button>
              )}
              {deleteEligible.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startBatch("delete", deleteEligible)}
                  className="h-7 text-[12px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
                >
                  Delete ({deleteEligible.length})
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="w-8 pl-4">
              <Checkbox
                checked={selectedIds.size === sortedMachines.length && sortedMachines.length > 0}
                onCheckedChange={toggleSelectAll}
                aria-label="Select all visible machines"
              />
            </TableHead>
            <TableHead className={cn("pl-2", headerClass)} onClick={() => toggleSort("id")}>
              ID <SortIcon column="id" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("name")}>
              Machine <SortIcon column="name" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("gpu_type")}>
              GPU <SortIcon column="gpu_type" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("provider")}>
              Provider{" "}
              <SortIcon column="provider" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("source")}>
              Type <SortIcon column="source" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("zone")}>
              Zone <SortIcon column="zone" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("cloud_status")}>
              Status{" "}
              <SortIcon column="cloud_status" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("driver_version")}>
              Driver{" "}
              <SortIcon column="driver_version" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className={headerClass} onClick={() => toggleSort("owner_name")}>
              Owner <SortIcon column="owner_name" sortKey={sortKey} sortDirection={sortDirection} />
            </TableHead>
            <TableHead className="pr-6 text-right text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedMachines.map((machine, index) => {
            const isFree = !machine.allocated_to && machine.cloud_status === "running";
            const isOwnedByMe = machine.allocated_to === currentUserId;
            const canRelease =
              isOwnedByMe || (isAdmin && machine.allocated_to != null) || machine.owner_is_service;
            const isKicking =
              (isAdmin || machine.owner_is_service) &&
              machine.allocated_to != null &&
              machine.allocated_to !== currentUserId;
            const isOwnedByOther = machine.allocated_to != null && !isOwnedByMe;
            const isStopped = machine.cloud_status === "stopped";
            const idle = formatIdleDuration(machine.idle_since);
            const showIdleCell = machine.cloud_status === "running";
            const agentDown =
              machine.agent_status === "stale" || machine.agent_status === "none"
                ? machine.agent_status
                : null;
            const startedAgo =
              machine.cloud_status === "running" ? formatStartedAgo(machine.started_at) : null;

            // Cluster info for this machine
            const clusterKey = clusterInfo.machineToCluster.get(machine.id);
            const clusterColor = clusterKey ? clusterInfo.clusterColors.get(clusterKey) : undefined;

            // Determine if this row is adjacent to another machine in the same cluster
            const prevMachine = index > 0 ? sortedMachines[index - 1] : null;
            const nextMachine =
              index < sortedMachines.length - 1 ? sortedMachines[index + 1] : null;
            const prevClusterKey = prevMachine
              ? clusterInfo.machineToCluster.get(prevMachine.id)
              : null;
            const nextClusterKey = nextMachine
              ? clusterInfo.machineToCluster.get(nextMachine.id)
              : null;
            const isFirstInCluster = clusterKey && prevClusterKey !== clusterKey;
            const isLastInCluster = clusterKey && nextClusterKey !== clusterKey;

            return (
              <TableRow
                key={machine.id}
                className={cn(
                  // Fixed height sized to the tallest cell state (the lg owner
                  // avatar), so rows don't reflow when acquire/release swaps
                  // "free" text for avatar + buttons.
                  "h-[58px] border-border/50 hover:bg-accent/50 transition-colors",
                  selectedIds.has(machine.id) && "bg-accent/30",
                )}
              >
                <TableCell className="w-8 pl-4 relative">
                  {clusterColor && (
                    <div
                      className={cn(
                        "absolute left-0 top-0 bottom-0 w-1",
                        clusterColor,
                        isFirstInCluster && "rounded-t",
                        isLastInCluster && "rounded-b",
                      )}
                    />
                  )}
                  {clusterKey ? (
                    <Tooltip>
                      <TooltipTrigger
                        type="button"
                        className="relative inline-flex items-center cursor-pointer"
                        onClick={(event) =>
                          handleClusterCheckboxClick(machine.id, clusterKey, event)
                        }
                      >
                        <Checkbox
                          checked={selectedIds.has(machine.id)}
                          aria-label={`Select ${machine.name}`}
                          className="pointer-events-none"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" className="text-xs py-1 px-2">
                        <div className="flex flex-col gap-0.5">
                          <span>Shift+click — select unassigned</span>
                          <span>Shift+double-click — select whole fabric</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      checked={selectedIds.has(machine.id)}
                      onCheckedChange={() => toggleSelect(machine.id)}
                      aria-label={`Select ${machine.name}`}
                    />
                  )}
                </TableCell>
                <TableCell
                  className={cn(
                    "pl-2 font-mono text-[12px] text-muted-foreground tabular-nums",
                    isStopped && "opacity-50",
                  )}
                >
                  <CopyableMachineId
                    id={machine.id}
                    name={machine.name}
                    ip={machineHost(machine)}
                  />
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  <div className="w-[260px] min-w-0">
                    <MachineNameLink
                      machine={machine}
                      currentUserId={currentUserId}
                      isAdmin={Boolean(isAdmin)}
                    />
                    <div className="flex min-w-0 items-center gap-1.5">
                      {/* IP keeps its full width; the note text absorbs the truncation. */}
                      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                        {machineHost(machine) ? (
                          <CopyableIp ip={machineHost(machine) as string} />
                        ) : (
                          "\u2014"
                        )}
                      </span>
                      <NoteButton machine={machine} canEdit={canEditNote(machine)} />
                    </div>
                    {showSshString && machineHost(machine) && machine.ssh_user && (
                      <code className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground">
                        ssh {machine.ssh_user}@{machineHost(machine)}
                      </code>
                    )}
                    {machine.k8s_cluster_name && (
                      <Link
                        to="/k8s-clusters"
                        className="mt-0.5 block truncate rounded text-[10px] font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
                      >
                        K8s: {machine.k8s_cluster_name}
                      </Link>
                    )}
                  </div>
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  <div className="flex flex-col leading-tight">
                    <span className="font-mono text-[13px] text-foreground/80">
                      {formatGpu(machine)}
                    </span>
                    {machine.price_per_hour != null && (
                      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                        ${machine.price_per_hour.toFixed(2)}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  <ProviderLogo provider={machine.provider} />
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  <div className="flex flex-col items-start gap-1">
                    <TypeBadge source={machine.source} />
                    {machine.pool_purpose != null && (
                      <PoolPurposeBadge purpose={machine.pool_purpose} />
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={cn("text-[13px] text-muted-foreground", isStopped && "opacity-50")}
                >
                  <div className="flex flex-col leading-tight">
                    <span>{machine.zone ?? "\u2014"}</span>
                    {machine.fabric && (
                      <span className="text-[11px] text-muted-foreground/60">{machine.fabric}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  <div className="flex flex-col leading-tight gap-0.5">
                    <StatusWithTooltip
                      status={machine.cloud_status}
                      isIdle={showIdleCell && idle !== null}
                      idleDuration={idle}
                      idleReason={machine.idle_reason}
                      startedAgo={startedAgo}
                      triggerClassName="self-start"
                    />
                    {showIdleCell && idle && machine.idle_reason === "engine_idle" && (
                      <span className="text-[10px] text-muted-foreground">no requests</span>
                    )}
                    {showIdleCell && idle && machine.idle_reason === "gpu_idle" && (
                      <span className="text-[10px] text-muted-foreground">GPU idle</span>
                    )}
                    {showIdleCell && agentDown && (
                      <Tooltip>
                        <TooltipTrigger className="inline-flex items-center gap-1 cursor-default">
                          <span
                            className={cn(
                              "inline-block h-2 w-2 rounded-full",
                              agentDown === "none" ? "bg-rose-400/60" : "bg-amber-400/70",
                            )}
                          />
                          <span className="text-[11px] text-muted-foreground/60">
                            {agentDown === "none" ? "no agent" : "agent down"}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {agentDown === "none"
                            ? "gpu-watchdog agent is not running on this machine"
                            : `gpu-watchdog stopped reporting ${formatAge(machine.last_heartbeat)} ago \u2014 machine data may be stale`}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <MaintenanceWarning machine={machine} />
                  </div>
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  {machine.driver_version && agentDown ? (
                    <Tooltip>
                      <TooltipTrigger className="cursor-default">
                        <span className="font-mono text-[11px] text-muted-foreground/40">
                          {machine.driver_version}
                          <span className="ml-1 text-amber-400/80">?</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {`Last value reported before the gpu-watchdog stopped${
                          formatAge(machine.last_heartbeat)
                            ? ` ${formatAge(machine.last_heartbeat)} ago`
                            : ""
                        } \u2014 may be outdated`}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {machine.driver_version || "\u2014"}
                    </span>
                  )}
                </TableCell>
                <TableCell className={cn(isStopped && "opacity-50")}>
                  {machine.owner_name && machine.allocated_to ? (
                    <UserResourcesDialog
                      ownerName={machine.owner_name}
                      ownerId={machine.allocated_to}
                      isService={machine.owner_is_service}
                      ownerAvatarUrl={machine.owner_avatar_url}
                    >
                      <div className="flex items-center gap-2">
                        <OwnerAvatar
                          name={machine.owner_name}
                          isService={machine.owner_is_service}
                          avatarUrl={machine.owner_avatar_url}
                          size="lg"
                        />
                        <span className="text-[13px] text-foreground/80">{machine.owner_name}</span>
                      </div>
                    </UserResourcesDialog>
                  ) : (
                    <span className="text-[13px] italic text-muted-foreground/50">
                      &mdash; free &mdash;
                    </span>
                  )}
                </TableCell>
                <TableCell className="pr-6 text-right">
                  <span className="inline-flex items-center gap-1.5">
                    {extraActions?.(machine)}
                    {isFree && <AcquireButton machine={machine} />}
                    {canRelease && isKicking && (
                      <ConfirmDialog
                        title={`Kick ${machine.owner_name} from ${machine.name}?`}
                        description={`This will release the machine from ${machine.owner_name}. They will be notified via Slack.`}
                        confirmLabel="Kick"
                        variant="destructive"
                        onConfirm={() => runSingleRelease(machine, queryClient)}
                        trigger={
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[12px] border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400 dark:hover:bg-amber-900"
                          >
                            Kick
                          </Button>
                        }
                      />
                    )}
                    {canRelease && !isKicking && (
                      <Button
                        size="sm"
                        onClick={() => runSingleRelease(machine, queryClient)}
                        variant="outline"
                        className="h-7 text-[12px] text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                      >
                        Release
                      </Button>
                    )}
                    {isOwnedByOther && !machine.owner_is_service && (
                      <TakeOrRequest
                        machine={machine}
                        machines={machines}
                        currentUserId={currentUserId}
                      />
                    )}
                    <MachineActionsMenu
                      machine={machine}
                      currentUserId={currentUserId}
                      isAdmin={isAdmin}
                      mutations={menuMutations}
                    />
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
          {sortedMachines.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={12} className="py-12 text-center text-muted-foreground">
                {hasFilter ? "No machines match your filter" : "No machines found"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {batchProgress && (
        <BatchProgressDialog
          machines={batchProgress.machines}
          action={batchProgress.action}
          deleteAfterStop={batchProgress.deleteAfterStop}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setBatchProgress(null);
              setSelectedIds(new Set());
            }
          }}
        />
      )}
      {batchRequest && (
        <BatchRequestProgressDialog
          machines={batchRequest.machines}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setBatchRequest(null);
              setSelectedIds(new Set());
            }
          }}
        />
      )}
      <NoteDialog
        machines={batchNoteMachines ?? []}
        open={batchNoteMachines !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setBatchNoteMachines(null);
        }}
        onDone={() => setSelectedIds(new Set())}
      />
      <AlertDialog
        open={pendingRequestMachines !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingRequestMachines(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRequestMachines
                ? `Request ${pendingRequestMachines.length} machine${pendingRequestMachines.length === 1 ? "" : "s"}?`
                : ""}
            </AlertDialogTitle>
            {pendingRequestMachines && (
              <AlertDialogDescription>
                {machineNamesPreview(pendingRequestMachines)} — Slack DMs go to{" "}
                {requestOwnersPreview(pendingRequestMachines)}. Owners have 30 minutes to accept or
                deny; otherwise the machines auto-release to you.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingRequestMachines) return;
                const machines = pendingRequestMachines;
                setPendingRequestMachines(null);
                setBatchRequest({ machines });
              }}
            >
              Send requests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingConfirm !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingConfirm?.title ?? ""}</AlertDialogTitle>
            {pendingConfirm?.description && (
              <AlertDialogDescription>{pendingConfirm.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={pendingConfirm?.destructive ? "destructive" : "default"}
              onClick={() => {
                if (!pendingConfirm) return;
                const { action, machines, deleteAfterStop } = pendingConfirm;
                setPendingConfirm(null);
                // Same toast-vs-modal split as above, after the kick confirm.
                if (action === "release") {
                  runBatchRelease(machines, queryClient);
                } else {
                  setBatchProgress({ action, machines, deleteAfterStop });
                }
                setSelectedIds(new Set());
              }}
            >
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
